import { useHistory } from 'react-router';
import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { Button } from '@belong/ui';
import { Confetti } from 'animations/Confetti/Confetti';
import TextBubbleWithUserProfile from 'components/TextBubbleWithUserProfile/TextBubbleWithUserProfile';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { EmployeeAssignmentTypes } from 'models/enums';
import { ReportType } from 'models/enums/typings';
import { getPricingAssignedEmployee } from 'pages/PostInspectionFlow/steps/Pricing/utils';
import { STRINGS } from 'post-inspection/constants/strings';
import { useEmployee } from 'post-inspection/hooks/use-employee';
import { EMPLOYEE_TITLES } from 'strings/common.strings';

type Props = {
  // `any[]`, not `unknown[]`: this arrives as an untyped step payload and is
  // passed straight to `useEmployee`, which needs to read into it.
  listings: any[];
  flow: {
    configuration: {
      homeUniqueId: string;
      flowSubType: string;
    };
    flowType: string;
  };
  isPreMoveOut: boolean;
  lastStepResource?: unknown;
};

export const ImprovementsReportSuccess = ({
  listings,
  flow,
  isPreMoveOut,
  lastStepResource,
}: Props) => {
  const { push, location } = useHistory();
  const { width } = useWindowSize();
  const { isEnabled: isAccountRedesignEnabled } =
    useFeatureFlag('account-redesign');

  const propertyId = flow?.configuration?.homeUniqueId;

  // Asserted: `useEmployee` returns `undefined` only for the unimplemented
  // Pricing report type, which this page never asks for.
  const { employee } = useEmployee({
    listings,
    flow,
    reportType: ReportType.ImprovementsSpecialist,
  })!;

  const pricingEmployee =
    flow.flowType === 'Pricing'
      ? getPricingAssignedEmployee(lastStepResource)
      : undefined;

  const formattedEmployee =
    flow.flowType === 'Pricing'
      ? pricingEmployee
      : {
          ...employee,
          // Cast because `models/enums` is plain JS, so its members widen to
          // `string` and cannot index `EMPLOYEE_TITLES`.
          jobTitle:
            EMPLOYEE_TITLES[
              EmployeeAssignmentTypes.HomeImprovementsSpecialist as keyof typeof EMPLOYEE_TITLES
            ],
        };

  const isMobile = width < BREAKPOINTS_WIDTHS.SM;

  const handleClick = () => {
    if (isAccountRedesignEnabled) {
      push('/redirect/flows/homeowner/post-auth');
    } else {
      push(`/accounts/portfolio/properties/${propertyId}`);
    }
  };

  const onboardingSuccessCopy =
    (location as { state: { from: string } })?.state?.from ===
    'ho-fixes-calendar'
      ? STRINGS['improvements-report-success.ho-fixes']
      : STRINGS['improvements-report-success'];

  return (
    <div className="flex h-screen w-screen justify-center">
      <Confetti />
      <div className="flex h-full w-full flex-col items-center px-sm pt-sm sm:max-w-[730px] sm:px-0 sm:pt-7xl">
        <TextBubbleWithUserProfile
          // Both ternaries above test the same `flowType`, so this is only
          // `undefined` to TypeScript, which narrows them independently.
          employee={formattedEmployee!}
          text={
            isPreMoveOut
              ? STRINGS['improvements-report-success.pre_move_out']
              : onboardingSuccessCopy
          }
        />
        <div className="mt-2xl w-full sm:w-auto">
          <Button onClick={handleClick} size={isMobile ? 'fluid' : 'large'}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};
